import {action, computed, observable, observe, toJS} from 'mobx';
import {evite, get} from 'evite';
import urls from 'urls';
import {getURLParameter} from '~/common/utils';

/**
 * used in case of error
 */
const EMPTY_WORDING = {
  thumbnail: null,
  category_name: 'not_found',
  text_singular: '',
  text_html: '<p>[Event Title]</p><p>Design your invitation here</p>',
  gallery_html: '',
};

export class TemplateStore {
  constructor(store, template, designStep = {}) {
    this.store = store;
    this.template = observable(template);

    // initial values for the designStep
    designStep.currentView = {
      front: 'card',
      envelopeSide: 'liner',
    };
    this.designStep = observable(designStep);
  }

  get(str) {
    return get(this, str);
  }

  @action
  setTemplate(template) {
    Object.assign(this.template, template);
  }

  async fetchTemplateListByName(categoryName) {
    const url = urls.get('ajax_gallery_category', {category: categoryName});
    const response = await evite.fetch(url);
    const json = await response.json();
    return json.templates;
  }

  async fetchTemplateListBySearch(search) {
    search = search.toLowerCase().replace(/\s+/g, ' ');
    const url = `${urls.get('ajax_gallery_search', {search})}&free=1`;
    const json = await evite.fetchJson(url);

    const message =
      typeof json === 'string'
        ? json
        : json && json.message
          ? json.message
          : !json
            ? 'An unknown error occurred'
            : '';
    if (message) {
      return Promise.reject(json);
    }

    return json;
  }

  getVersionName() {
    const name = getURLParameter('v');
    if (name) return name;

    try {
      return this.store.event.options.design.version;
    } catch (e) {
      evite.warn(e);
      evite.warn('Invalid event options');
    }

    return null;
  }

  // premium support functions
  getVersion(name = null) {
    name = name || this.getVersionName();

    if (!name) {
      // just return the first version
      return this.template.versions[0];
    }

    for (const version of this.template.versions) {
      if (version.name === name) return version;
    }

    return null;
  }

  getDefaultEnvelope() {
    return this.template.envelopes[0] || null;
  }

  getDefaultLiner() {
    const defaultEnv = this.getDefaultEnvelope();
    if (defaultEnv && defaultEnv.liners[1]) return defaultEnv.liners[1];

    return null;
  }

  setLiner(selectedEnvelopeName) {
    if (this.getEnvelope().liner) {
      this.store.event.options.design.liner = this.getEnvelope().liner.name;
    } else {
      // set a new default liner if the user previously had a liner
      // selected that is not available for the new envelope selection
      const selectedEnvelope = this.template.envelopes.find(
        (envelope) => envelope.name === selectedEnvelopeName
      );
      this.store.event.options.design.liner = selectedEnvelope.liners[1]
        ? selectedEnvelope.liners[1].name
        : selectedEnvelope.liners[0].name;
    }
  }

  setEnvelopeTextColor(selectedEnvelopeName) {
    this.store.event.options.design.name_style.color =
      this.getEnvelope(selectedEnvelopeName).textColor;
  }

  /**
   * Get the wording from the template for the wording_type.
   *
   * (wording_type is either invitation or card)
   */
  getWording(wordingType, category, versionName = null) {
    const version = this.getVersion(versionName);
    if (!version) return EMPTY_WORDING;

    if (!version.wordings[wordingType]) {
      // just use the first wording to avoid an error
      wordingType = Object.keys(version.wordings)[0];
    }

    const wordings = version.wordings[wordingType];
    if (wordings[category]) {
      return wordings[category];
    }

    if (wordings.default) {
      return wordings.default;
    }

    evite.error(
      `Premium template ${this.template.name} missing wording for ${wordingType}, ${category}`
    );
    if (Object.keys(wordings)) {
      return wordings[Object.keys(wordings)[0]];
    }

    return EMPTY_WORDING;
  }

  getEnvelope(name = null) {
    const {design} = this.store.event.options;
    if (!design) return null;
    try {
      name = name || this.store.event.options.design.envelope;
    } catch (e) {
      evite.warn(e);
      evite.warn('Invalid event options');
    }

    let env = null;
    for (let i = 0; i < this.template.envelopes.length; i++) {
      const current = this.template.envelopes[i];
      if (current.name === name) {
        env = toJS(current);
        break;
      }
    }
    if (!env) {
      return null;
    }

    // finally, setup the liner and stamp for easier access
    if (design.stamp === 'custom') {
      env.stamp = {...env.stamps[0]};
      env.stamp.name = 'custom';
      env.stamp.displayName = 'custom_stamp';
      env.stamp.image = design.custom_stamp;
      env.icon = design.custom_stamp;
    } else {
      for (const s of env.stamps) {
        if (s.name === design.stamp) env.stamp = s;
      }
    }
    if (!env.stamp) {
      env.stamp = env.stamps[env.stamps.length - 1];
    }

    for (const liner of env.liners) {
      if (liner.name === design.liner) env.liner = liner;
    }
    if (!env.liner) {
      env.liner = env.liners[env.liners.length - 1];
    }
    return env;
  }

  // update the designStep store with current
  // view shown; i.e., 'card', 'liner'
  @action
  setCurrentView(cardOrEnvelope, envelopeOrLiner) {
    this.designStep.currentView = {
      front: cardOrEnvelope,
      envelopeSide: envelopeOrLiner,
    };
  }

  // optionType: 'envelope', 'liner', 'stamp', 'branding'
  @action
  setCurrentOption(item, optionType) {
    if (optionType === 'custom_logo') {
      const {envelopeSide} = this.designStep.currentView;
      this.setCurrentView('card', 'envelope');
    } else {
      // First, Set the item on the store
      this.store.event.options.design[optionType] = item.name;
      // Second, do the interface-y things.
      if (optionType === 'envelope') {
        this.setLiner(item.name);
        this.setEnvelopeTextColor(item.name);
        this.setCurrentView('envelope', 'liner');
      } else if (optionType === 'liner') {
        this.setCurrentView('envelope', 'liner');
      } else if (optionType === 'stamp' || optionType === 'custom_stamp') {
        this.setCurrentView('envelope', 'envelope');
      } else if (optionType === 'version') {
        const {envelopeSide} = this.designStep.currentView;
        this.setCurrentView('card', envelopeSide);
      }
    }
  }

  /**
   * Set the width of the custom logo
   * @param width - width of the custom logo
   * @param layout - desktop or mobile
   */
  @action
  setCustomLogoWidth(width, layout) {
    const logo = {...this.store.event.custom_logo};
    const logoInfo = logo[layout];
    if (logoInfo) {
      logoInfo.width = width;
    } else {
      logo[layout] = {
        width,
      };
    }
    this.store.event.custom_logo = logo;
  }

  /**
   * Set X,Y coordinates of the custom logo for desktop or mobile
   * @param coordinates - {x: 20, y:20}
   * @param layout - desktop, mobile
   */
  @action
  setCustomLogoPosition(layout, coordinates) {
    const logo = {...this.store.event.custom_logo};
    const logoInfo = logo[layout];
    if (logoInfo) {
      logoInfo.x = coordinates.x;
      logoInfo.y = coordinates.y;
    } else {
      logo[layout] = {
        x: coordinates.x,
        y: coordinates.y,
      };
    }
    this.store.event.custom_logo = logo;
  }

  @action
  saveCustomLogo(layout, logoData) {
    const logo = {...this.store.event.custom_logo};
    const logoInfo = logo[layout];
    if (logoInfo) {
      logoInfo.x = logoData.x;
      logoInfo.y = logoData.y;
      logoInfo.width = logoData.width;
    } else {
      logo[layout] = logoData;
    }

    this.store.event.custom_logo = logo;
  }

  @action
  updateCustomLogo(layout, logoData) {
    const logo = {...this.store.event.custom_logo};
    if (logo[layout]) {
      logo[layout] = {
        ...logo[layout],
        ...logoData,
      };
      this.store.event.custom_logo = logo;
    }
  }
}
